import { ReactNode, useEffect, useState } from 'react';

export type TabItem = {
  title: string;
  hidden?: boolean;
  contents: ReactNode;
  unreads?: number;
  id?: string;
};
type TabsProps = {
  full?: boolean;
  tabItems: TabItem[];
  hidden?: boolean;
  unreads?: number;
  initialTab?: number;
};

export function TabButton({
  title,
  hidden,
  onClick,
  isCurrent,
  unreads,
}: {
  title: string;
  hidden?: boolean;
  onClick: () => void;
  isCurrent: boolean;
  unreads?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-fit items-center justify-center whitespace-nowrap rounded-full px-6 py-2 font-semibold transition-colors focus:outline-none sm:px-[1.6rem] lg:px-[3.2rem] ${
        isCurrent
          ? 'bg-black text-white'
          : 'border border-[#D9D9D9] bg-transparent text-black hover:text-brand-secondary focus:text-brand-secondary'
      }

      ${hidden ? 'hidden' : 'block'}
        `}
    >
      <div className="flex flex-row-reverse items-center gap-x-2">
        <span className="text-[1.8rem]">{title}</span>

        {!!unreads && (
          <div className="flex h-[1.8rem] min-w-[1.8rem] items-center justify-center rounded-full bg-brand-secondary px-[0.4rem] py-[0.2rem] text-[1.2rem] font-semibold text-brand-white">
            {unreads}
          </div>
        )}
      </div>
    </button>
  );
}

const Tabs = ({ tabItems: tabs, full, unreads, initialTab }: TabsProps) => {
  const [current, setCurrent] = useState<number>(initialTab ?? 0);
  const tabNavItems = tabs.map((tab) => tab.title);

  useEffect(() => {
    // append the current tab to the url
    const curr = tabs[current]?.title;
    if (!curr) return;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabs[current].title.toLowerCase());
    window.history.replaceState({}, '', url.toString());
  }, [current, tabs]);

  return (
    <div>
      <div
        className={`flex justify-between w-${
          full ? 'full' : 'min'
        } overflow-x-auto`}
      >
        {tabNavItems.map((item, i) => {
          const isCurrent = current === i;
          return (
            <TabButton
              key={item}
              hidden={tabs[i].hidden}
              title={item}
              onClick={() => setCurrent(i)}
              isCurrent={isCurrent}
              unreads={unreads}
            />
          );
        })}
      </div>
      <hr className="w-full" />
      {/* Render all tabs and conditionally hide so that it doesn't have to re-mount the contents on tab change */}
      {tabs.map((tab, i) => {
        return (
          <div
            key={tab.title}
            className={`${current === i ? 'contents' : 'hidden'}`}
          >
            {tab.contents}
          </div>
        );
      })}
    </div>
  );
};

export default Tabs;
